import React from 'react';
import { Board, EMPTY } from '@/types/game';

interface GameBoardProps {
    board: Board;
    validMoves: [number, number][];
    flippingPieces: [number, number][];
    onCellClick: (row: number, col: number) => void;
    onCellRightClick?: (row: number, col: number) => void;
    lastMovePosition?: [number, number] | null;
    previewFlips?: [number, number][];
    gameEndAnimation?: boolean;
}

export const GameBoard: React.FC<GameBoardProps> = ({
    board,
    validMoves,
    flippingPieces,
    onCellClick,
    onCellRightClick,
    lastMovePosition,
    previewFlips = [],
    gameEndAnimation = false
}) => {
    const isValidMove = (row: number, col: number): boolean => {
        return validMoves.some(([r, c]) => r === row && c === col);
    };

    const isFlipping = (row: number, col: number): boolean => {
        return flippingPieces.some(([r, c]) => r === row && c === col);
    };

    const isLastMove = (row: number, col: number): boolean => {
        return lastMovePosition ? lastMovePosition[0] === row && lastMovePosition[1] === col : false;
    };

    const isPreviewFlip = (row: number, col: number): boolean => {
        return previewFlips.some(([r, c]) => r === row && c === col);
    };

    if (!board || !Array.isArray(board) || board.length === 0) {
        return (
            <div className="w-96 h-96 bg-red-200 flex items-center justify-center">
                <p>ボードデータがありません</p>
            </div>
        );
    }

    return (
        <div className="w-full flex justify-center">
            <div className={`grid grid-cols-8 gap-0 w-96 h-96 bg-green-600 border-2 border-green-800 rounded-lg overflow-hidden shadow-xl transition-all duration-1000 ${gameEndAnimation ? 'animate-pulse border-yellow-400 border-4' : ''}`}>
                {board.map((row, rIdx) => (
                    row.map((cell, cIdx) => {
                        const isValidCell = isValidMove(rIdx, cIdx);
                        const isLastMoveCell = isLastMove(rIdx, cIdx);
                        const isPreviewFlipCell = isPreviewFlip(rIdx, cIdx);
                        return (
                            <div
                                key={`${rIdx}-${cIdx}`}
                                className={`
                                    aspect-square border border-green-700 flex items-center justify-center cursor-pointer
                                    hover:bg-green-500 transition-colors duration-200
                                    ${isValidCell ? 'bg-green-400 hover:bg-green-300' : ''}
                                    ${isLastMoveCell ? 'ring-2 ring-red-500 ring-inset' : ''}
                                `}
                                onClick={() => onCellClick(rIdx, cIdx)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    onCellRightClick?.(rIdx, cIdx);
                                }}
                            >
                                {cell !== EMPTY && (
                                    <div
                                        className={`
                                            w-10 h-10 rounded-full shadow-lg transition-transform duration-300
                                            ${cell === 1 ? 'bg-black' : 'bg-white'}
                                            ${isFlipping(rIdx, cIdx) ? 'animate-spin' : ''}
                                            ${isPreviewFlipCell ? 'animate-pulse ring-2 ring-yellow-400' : ''}
                                            ${gameEndAnimation ? 'animate-bounce' : ''}
                                        `}
                                    />
                                )}
                                {isValidCell && (
                                    <div className="w-6 h-6 rounded-full bg-yellow-400 opacity-60" />
                                )}
                            </div>
                        );
                    })
                ))}
            </div>
        </div>
    );
}; 
